import { ApiClient } from "@api/ApiClient";
import { useApi } from "@api/ApiProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface EulaResponse {
  eula_id: string;
  eula_text: string;
  accepted: boolean;
}

interface AcceptEulaResponse {
  success: boolean;
  message?: string;
}

interface UseEulaReturn {
  eulaData: EulaResponse | null;
  isAccepted: boolean;
  isLoading: boolean;
  isError: boolean;
  handleAcceptEula: (eulaId: number) => Promise<void>;
}

export const useEula = (): UseEulaReturn => {
  const { api, needsReauth } = useApi();
  const queryClient = useQueryClient();

  // Fetch EULA data
  const {
    data: eulaData = null,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["eula"],
    queryFn: () => getEula(api),
    enabled: !needsReauth,
  });

  // Mutation for accepting EULA
  const { mutateAsync: acceptEulaMutation, isPending: isAcceptingEula } = useMutation({
    mutationFn: (eulaId: number) => acceptEula(api, eulaId),
    onSuccess: () => {
      // Update the cache to mark EULA as accepted
      queryClient.setQueryData(
        ["eula"],
        (oldData: EulaResponse | null) =>
          ({
            ...oldData,
            accepted: true,
          }) as EulaResponse
      );
    },
    onError: (error) => {
      console.error("Error accepting EULA:", error);
    },
  });

  const handleAcceptEula = async (eulaId: number): Promise<void> => {
    await acceptEulaMutation(eulaId);
  };

  // Derive isAccepted from eulaData: null means accepted, or check accepted property
  const isAccepted = eulaData?.accepted === true;

  return {
    eulaData,
    isAccepted,
    isLoading: !needsReauth && (isLoading || isAcceptingEula),
    isError: needsReauth || isError,
    handleAcceptEula,
  };
};

// TODO: Get Rid of apiClient as parameter for the API calls
export const getEula = async (apiClient: ApiClient): Promise<EulaResponse> => {
  const results = await apiClient.get("/eula");
  if (!results) {
    throw new Error("No response from server");
  }
  return results.json() as Promise<EulaResponse>;
};
// TODO: Get Rid of apiClient as parameter for the API calls
export const acceptEula = async (apiClient: ApiClient, eulaId: number): Promise<AcceptEulaResponse> => {
  const props = { headers: { "Content-Type": "application/json" } };
  const body = JSON.stringify({ eulaId });
  const results = await apiClient.post("/eula", body, props);
  if (!results) {
    throw new Error("No response from server");
  }
  return results.json();
};
